<template>
  <v-container fluid class="pt-4" v-if="currentAgent">
    <h4 class="text-center text-subtitle-1 font-weight-bold">{{ $t('label.totalCashback') }}</h4>
    <p class="text-center text-subtitle-1 font-weight-bold">{{ currentAgent.currency }} {{ formatPrice(currentAgent.total_cashback) }}</p>
    <div class="d-flex justify-center" id="top-of-table" :class="$vuetify.breakpoint.smAndUp ? 'w-100 flex-row' : 'flex-column align-center'">
      <v-btn
        height="unset"
        class="white--text text-transform-none font-weight-bold green-gradient-down"
        :width="$vuetify.breakpoint.smAndUp ? 'auto' : '15rem'"
        :class="$vuetify.breakpoint.smAndUp ? 'px-8 py-1 mr-8' : 'py-1 px-2 my-4'"
        :to="{ name: $constants.ROUTE_NAME.AGENT_CONVERT_POINT }"
      >
        <img src="@/assets/coin-2x.png" :width="!$vuetify.breakpoint.smAndUp ? '40px' : 'auto'" />
        {{ $t('label.convertToPoints') }}
      </v-btn>
      <v-btn
        height="unset"
        class="white--text text-transform-none font-weight-bold green-gradient-down"
        :width="$vuetify.breakpoint.smAndUp ? 'auto' : '15rem'"
        :class="$vuetify.breakpoint.smAndUp ? 'px-8 py-1' : 'py-1 px-2'"
        :to="{ name: $constants.ROUTE_NAME.AGENT_TRANSFER_TO_BANK }"
      >
        <img src="@/assets/bank.png" :width="!$vuetify.breakpoint.smAndUp ? '40px' : 'auto'" />
        {{ $t('label.transferToBank') }}
      </v-btn>
    </div>
    <v-divider class="mt-8 mb-2" />
    <v-container class="py-4" :class="$vuetify.breakpoint.smAndUp ? 'px-10' : 'px-2'">
      <div>
        <h4 class="text-subtitle-1 font-weight-bold">{{ $t('label.transactionHistory') }}</h4>
        <div class="d-flex flex-row justify-space-between flex-wrap">
          <div class="order-2 order-sm-1">
            <v-dialog ref="dialog" v-model="modal" persistent width="290px" @click:outside="modal = false">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="dateRangeText"
                  :placeholder="$t('placeholder.filterByDate')"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  dense
                  single-line
                  hide-details
                  class="filter-dropdown white no-input-underline mr-8"
                  append-icon="mdi-chevron-down"
                  @click:append="modal = true"
                  flat
                ></v-text-field>
              </template>
              <v-date-picker v-model="filters.dateRange" type="month" multiple range color="primary lighten-1" header-color="primary" scrollable>
                <v-spacer></v-spacer>
                <v-btn text color="primary" @click="modal = false">{{ $t('button.cancel') }}</v-btn>
                <v-btn text color="primary" @click="fetchTransaction">{{ $t('button.ok') }}</v-btn>
              </v-date-picker>
            </v-dialog>
            <v-select
              v-model="filters.type"
              :items="placeholder.type"
              :placeholder="$t('placeholder.filterByAction')"
              dense
              single-line
              hide-details
              class="transparent filter-dropdown no-input-underline"
              @change="onFilterTypeChange"
            >
              <template #append>
                <v-icon small class="transparent">mdi-chevron-down</v-icon>
              </template>
              <template #item="{ item }">
                <div class="filter-dropdown--item">
                  {{ item.text }}
                </div>
              </template>
            </v-select>
          </div>
          <v-btn small color="white" @click="downloadPDF" class="order-1 order-md-2">
            <div style="width: 44px"><DownloadPDFIcon /></div>
          </v-btn>
        </div>
        <v-data-table
          :items="transactionsWithoutRepeatedDates"
          :headers="placeholder.headers"
          :item-class="rowClasses"
          hide-default-footer
          :page.sync="page"
          class="transparent transaction-history-table"
          v-if="$vuetify.breakpoint.smAndUp"
        >
          <template #item.date="{ item }">
            <span>{{ item.date ? date(item.date) : '' }}</span>
          </template>
          <template #item.time="{ item }">
            <span>{{ item.time | shortTime }}</span>
          </template>
          <template #item.status="{ item }">
            <v-chip small :class="{ 'primary lighten-1': item.status === 'Successful' }">
              {{ $t(`label.${item.status}`) }}
            </v-chip>
          </template>
          <template #item.transaction_type="{ item }">
            <span>{{ $t(`label.${item.transaction_type.replaceAll(' ', '')}`) }}</span>
          </template>
          <template #item.amount="{ item }">
            <span class="orange--text font-weight-bold text-body">{{ item.amount }}</span>
          </template>
          <template #footer="{ props: { pagination } }">
            <div class="trx-history-footer d-flex align-center justify-end my-4 mr-6">
              <button fab depressed color="transparent" x-small @click="prevPage">
                <v-icon class="transparent">mdi-arrow-left</v-icon>
              </button>
              <v-text-field v-model="page" dense single-line hide-details outlined class="transparent page-input">
                <template #append-outer>
                  <span class="text-no-wrap">of {{ pagination.pageCount }}</span>
                </template>
              </v-text-field>
              <button fab depressed color="transparent" x-small @click="nextPage(pagination.pageCount)">
                <v-icon class="transparent">mdi-arrow-right</v-icon>
              </button>
            </div>
          </template>
        </v-data-table>
        <div v-if="!$vuetify.breakpoint.smAndUp">
          <v-card v-for="(item, i) in placeholder.list" :key="i" class="my-4 white">
            <v-card-title class="primary--text text--darken-1 text-subtitle-1 pb-1 font-weight-medium">{{ item.date ? date(item.date) : '' }}</v-card-title>
            <v-card-text v-for="(record, j) in item.list" :key="j" class="pt-0">
              <div class="d-flex flex-row justify-space-between">
                <span>{{ record.time | shortTime }}</span>
                <v-chip x-small :class="{ 'primary lighten-1': record.status === 'Successful' }">
                  {{ record.status }}
                </v-chip>
              </div>
              <div class="d-flex flex-row justify-space-between">
                <div class="black--text font-weight-medium">
                  {{ record.transaction_type }}
                </div>
                <span class="orange--text font-weight-bold text-body">{{ record.amount }}</span>
              </div>
              <span>{{ record.transaction_number }}</span>
            </v-card-text>
          </v-card>
        </div>
      </div>
    </v-container>
  </v-container>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import moment from 'moment'
import { localeHelper, uiHelper } from '@/utils'
import DownloadPDFIcon from '@/assets/icons/download-pdf.svg'

export default {
  components: { DownloadPDFIcon },
  data: () => ({
    page: 1,
    filters: {
      dateRange: null,
      type: null
    },
    months: [],
    modal: false,
    placeholder: {
      type: [
        { text: localeHelper.getMessage('label.point'), value: 1 },
        { text: localeHelper.getMessage('label.cash'), value: 2 }
      ],
      transactions: [],
      list: [],
      headers: [
        { text: localeHelper.getMessage('label.date'), value: 'date' },
        { text: localeHelper.getMessage('label.time'), value: 'time' },
        { text: localeHelper.getMessage('label.transactionNumber'), value: 'transaction_number' },
        {
          text: localeHelper.getMessage('label.action'),
          value: 'transaction_type'
        },
        {
          text: '',
          value: 'status'
        },
        { text: 'SGD', value: 'amount' }
      ]
    }
  }),
  created() {
    this.filters.type = this.placeholder.type[0].value
    this.filters.dateRange = []
    // this.filters.dateRange = [moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'), moment().endOf('month').format('YYYY-MM-DD')]
    this.fetchTransaction()
  },
  computed: {
    ...mapState('agent', ['currentAgent']),
    dateRangeText() {
      let formatedMonths = []
      this.filters.dateRange.map((f) => formatedMonths.push(moment(f).format('MMM YYYY')))
      return formatedMonths.join(' ~ ')
    },
    transactionsWithoutRepeatedDates() {
      const datesAlreadyIncluded = []
      return this.placeholder.transactions.map((trx) => {
        if (datesAlreadyIncluded.includes(trx.date)) {
          trx.date = ''
        } else {
          datesAlreadyIncluded.push(trx.date)
        }
        return trx
      })
    }
  },

  methods: {
    ...mapActions('agent', ['getAgentTransaction', 'downloadTransactionHistoryFile']),
    onFilterTypeChange() {
      this.filters.dateRange = []
      this.fetchTransaction()
    },
    async fetchTransaction() {
      this.modal = false
      await this.getAgentTransaction({ type: this.filters.type, start_date: this.filters.dateRange[0], end_date: this.filters.dateRange[1] }).then((res) => {
        this.placeholder.transactions = []
        if (this.filters.dateRange.length === 0 && res.min_date && res.max_date)
          this.filters.dateRange = [moment(res.max_date).subtract(1, 'month').startOf('month').format('YYYY-MM-DD'), moment(res.max_date).endOf('month').format('YYYY-MM-DD')]
        res.transaction.map((f) => {
          this.placeholder.transactions = this.placeholder.transactions.concat(f.list)
        })
        this.placeholder.list = res.transaction
        console.log('RES', res)
      })
    },
    rowClasses(rowItem) {
      if (rowItem.date) {
        return 'has-date'
      }
    },
    prevPage() {
      if (this.page > 1) this.page--
      this.$nextTick(() => {
        this.$el.querySelector('#top-of-table').scrollIntoView({ behavior: 'smooth' })
      })
    },
    nextPage(totalPages) {
      if (this.page < totalPages) this.page++
      this.$nextTick(() => {
        this.$el.querySelector('#top-of-table').scrollIntoView({ behavior: 'smooth' })
      })
    },
    async downloadPDF() {
      await this.downloadTransactionHistoryFile({
        type: this.filters.type,
        start_date: this.filters.dateRange[0],
        end_date: this.filters.dateRange[1],
        export: 'pdf'
      }).then((response) => {
        const url = window.URL.createObjectURL(new Blob([response]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `transaction_history.pdf`)
        document.body.appendChild(link)
        link.click()
      })
    },
    formatPrice(val) {
      return uiHelper.priceFormatter(val)
    },
    groupBy(x, f) {
      return x.reduce((a, b) => ((a[f(b)] ||= []).push(b), a), {})
    },
    date(dateString) {
      return this.$options.filters.shortDate(dateString, 'DD/MM/YYYY')
    }
  }
}
</script>

<style lang="scss" scoped>
/* clears default header and middle table row borders */
::v-deep .v-data-table {
  th,
  td {
    border-bottom: unset !important;
  }

  /* add top border for rows with dates */
  .has-date:not(:first-of-type) td {
    border-top: thin solid $shadow-key-ambient-opacity;
  }
}

::v-deep .transaction-history-table .v-data-table__wrapper {
  min-height: 20rem;
}

::v-deep .page-input {
  width: 5rem;
  flex-grow: 0;

  .v-input__slot {
    padding-left: 0.4rem !important;
    padding-right: 0.4rem !important;

    input {
      text-align: right;
    }
  }

  .v-input__append-outer {
    margin-top: auto !important;
    margin-bottom: auto !important;
  }
}
</style>
